import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { Image } from 'expo-image';
import { Member } from '@/models/Member';
import { MemberDetails } from '~/models/MemberDetails';
import { MembersService } from '@/services/domain/MembersService';
import { EventsService } from '@/services/domain/EventsService';
import { useIsLargeScreen } from '~/hooks/useIsLargeScreen';
import { usePathname } from 'expo-router';
import { getMemberPath } from '@/constants/routes';
import { Tooltip, TooltipContent } from '~/components/gluestack/tooltip';
import { VStack } from '~/components/gluestack/vstack';
import { HStack } from '~/components/gluestack/hstack';
import { Skeleton } from '@/components/gluestack/skeleton';

interface ProfileTooltipProps {
  member: Member | null | undefined;
  children: React.ReactNode;
  isActive?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top left' | 'top right' | 'bottom left' | 'bottom right';
  openDelay?: number;
  closeDelay?: number;
}

const MAX_PHOTO_PREVIEW_COUNT = 5;

export function ProfileTooltip({
  member,
  children,
  isActive = false,
  placement = 'top left',
  openDelay = 500,
  closeDelay = 100,
}: ProfileTooltipProps) {
  const isLargeScreen = useIsLargeScreen();
  const pathname = usePathname();
  const memberUserName = member?.UserName ?? '';
  const memberIconUrl = member?.IconUrl ?? '';
  const isEvent = member?.IsEvent ?? false;
  const isCurrentProfile = !!memberUserName && pathname?.toLowerCase() === getMemberPath({ UserName: memberUserName, IsEvent: isEvent }).toLowerCase();
  const showTooltip = isActive && isLargeScreen && !!memberUserName && !isCurrentProfile;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const queryKey = isEvent ? ['event-profile', memberUserName] : ['member-details', memberUserName];

  const { data: memberDetails, isPending } = useQuery<MemberDetails>({
    queryKey,
    queryFn: () => isEvent
      ? EventsService.getEventDetails(memberUserName)
      : MembersService.getMemberDetailsByUsername(memberUserName),
    staleTime: 5 * 60 * 1000,
    enabled: showTooltip && isTooltipOpen,
  });

  if (!showTooltip) {
    return <>{children}</>;
  }

  // Lazy require to avoid circular dependencies back to ProfilePicture
  const { ProfileOverview } =
    require('./ProfileOverview') as typeof import('./ProfileOverview');
  const { EventOverview } =
    require('../events/EventOverview') as typeof import('../events/EventOverview');
  const { ProfilePhotosPreview } =
    require('./ProfilePhotosPreview') as typeof import('./ProfilePhotosPreview');

  return (
    <Tooltip
      isOpen={isTooltipOpen}
      onOpen={() => setIsTooltipOpen(true)}
      onClose={() => setIsTooltipOpen(false)}
      placement={placement as any}
      openDelay={openDelay}
      closeDelay={closeDelay}
      trigger={(triggerProps) => <View {...triggerProps}>{children}</View>}
    >
      <TooltipContent className="rounded-xl p-0 shadow-strong">
        <View className="relative min-w-0 overflow-hidden rounded-xl dark:ring-2 dark:ring-outline-200 bg-background-0">
          {(memberDetails?.PhotoUrl ?? memberIconUrl) ? (
            <>
              <Image
                source={{ uri: memberDetails?.PhotoUrl ?? memberIconUrl }}
                className="absolute inset-0 h-full w-full"
                contentFit="cover"
              />
              <View
                className="absolute inset-0 backdrop-blur-xl bg-background-0/60"
                pointerEvents="none"
              />
            </>
          ) : null}
          <VStack className="relative gap-3 p-4 pt-2">
            {isPending ? (
              <>
                <VStack className="gap-2">
                  <Skeleton className="h-6 w-40 rounded-md" />
                  <HStack className="gap-3">
                    <Skeleton className="h-4 w-20 rounded-md" />
                    <Skeleton className="h-4 w-20 rounded-md" />
                  </HStack>
                  <Skeleton className="h-4 w-32 rounded-md" />
                </VStack>
                <HStack className="gap-2.5 h-20">
                  {Array.from({ length: MAX_PHOTO_PREVIEW_COUNT }).map((_, i) => (
                    <Skeleton key={i} className="h-full aspect-square rounded-lg" />
                  ))}
                </HStack>
              </>
            ) : memberDetails ? (
              <>
                {isEvent && memberDetails.EventDetails ? (
                  <EventOverview event={memberDetails.EventDetails} />
                ) : (
                  <ProfileOverview member={memberDetails} title={memberDetails.UserName} />
                )}
                <ProfilePhotosPreview
                  memberId={memberDetails.Id}
                  publicPhotosCount={memberDetails.PublicPhotosCount ?? 0}
                  disableImageViewer
                  includeProfilePhoto
                  maxCount={MAX_PHOTO_PREVIEW_COUNT}
                  showLoadingSkeleton
                  skeletonCount={Math.min(memberDetails.PublicPhotosCount ?? 0, MAX_PHOTO_PREVIEW_COUNT)}
                  size="sm"
                  withDetails={false}
                />
              </>
            ) : null}
          </VStack>
        </View>
      </TooltipContent>
    </Tooltip>
  );
}
